import { useState, useEffect } from 'react'

import { BREAKPOINTS } from '@/constants/app'

export interface ResponsiveState {
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
    width: number
    height: number
    orientation: 'portrait' | 'landscape'
}

export const useResponsive = (): ResponsiveState => {
    const [state, setState] = useState<ResponsiveState>(() => {
        if (typeof window === 'undefined') {
            return {
                isMobile: false,
                isTablet: false,
                isDesktop: true,
                width: 1024,
                height: 768,
                orientation: 'landscape',
            }
        }

        const width = window.innerWidth
        const height = window.innerHeight

        return {
            isMobile: width < BREAKPOINTS.MD,
            isTablet: width >= BREAKPOINTS.MD && width < BREAKPOINTS.LG,
            isDesktop: width >= BREAKPOINTS.LG,
            width,
            height,
            orientation: height > width ? 'portrait' : 'landscape',
        }
    })

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth
            const height = window.innerHeight

            setState({
                isMobile: width < BREAKPOINTS.MD,
                isTablet: width >= BREAKPOINTS.MD && width < BREAKPOINTS.LG,
                isDesktop: width >= BREAKPOINTS.LG,
                width,
                height,
                orientation: height > width ? 'portrait' : 'landscape',
            })
        }

        window.addEventListener('resize', handleResize)
        window.addEventListener('orientationchange', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleResize)
        }
    }, [])

    return state
}

// Utility hook for specific breakpoint checks
export const useBreakpoint = (breakpoint: keyof typeof BREAKPOINTS): boolean => {
    const [matches, setMatches] = useState(false)

    useEffect(() => {
        const query = `(min-width: ${BREAKPOINTS[breakpoint]}px)`
        const media = window.matchMedia(query)

        setMatches(media.matches)

        const listener = (e: MediaQueryListEvent) => setMatches(e.matches)
        media.addEventListener('change', listener)

        return () => media.removeEventListener('change', listener)
    }, [breakpoint])

    return matches
}
